import React from 'react';
import { Chip, CircularProgress } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * AI Candidate Chip Component
 *
 * Displays AI-powered suggestions for experiments with purple gradient styling.
 * Used in CreateExperimentForm for discount and replacement suggestions.
 *
 * @param {string} text - The suggestion text (supports **bold** markers)
 * @param {function} onClick - Click handler (makes chip clickable)
 * @param {boolean} selected - Shows selected state (rainbow icon)
 * @param {boolean} isTextChip - Non-clickable text display (for main recommendation)
 * @param {boolean} loading - Shows loading spinner
 * @param {boolean} variant - 'default' for purple gradient, 'candidate' for white bg compact style
 * @param {boolean} fullWidth - Makes chip take full width
 */
const AICandidateChip = ({ text, onClick, selected, isTextChip, loading, variant = 'default', fullWidth }) => {
  // Parse **bold** markers in text
  const renderText = (text) => {
    if (!text) return null;
    return text.split(/\*\*(.*?)\*\*/).map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  const isCandidate = variant === 'candidate';

  // Colors based on variant
  const colors = isCandidate
    ? {
        background: '#FFFFFF',
        color: '#374151',
        border: '#E5E7EB',
        hoverBg: '#F9FAFB',
        shadow: 'rgba(0, 0, 0, 0.1)'
      }
    : {
        background: selected
          ? 'linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)'
          : 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
        color: '#7C3AED',
        border: selected ? '#7C3AED' : '#C4B5FD',
        hoverBg: 'linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)',
        shadow: 'rgba(124, 58, 237, 0.2)'
      };

  const isClickable = onClick && !isTextChip && !loading;

  // Rainbow gradient for selected candidate icon
  const rainbowGradient = 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 25%, #4ECDC4 50%, #45B7D1 75%, #A855F7 100%)';

  const icon = loading ? (
    <CircularProgress size={12} sx={{ color: '#7C3AED' }} />
  ) : (
    <AutoAwesomeIcon
      sx={{
        fontSize: 12,
        ...(isCandidate && selected
          ? {
              background: rainbowGradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }
          : {
              color: '#7C3AED !important'
            }
        )
      }}
    />
  );

  // For clickable chips, stop propagation and call handler
  // For non-clickable chips (isTextChip), let events bubble to parent
  const handleClick = (e) => {
    if (isClickable && onClick) {
      e.stopPropagation();
      onClick();
    }
    // For text chips, don't stop propagation so parent click handlers work
  };

  return (
    <Chip
      icon={icon}
      label={renderText(text)}
      size="small"
      onClick={handleClick}
      sx={{
        background: colors.background,
        color: colors.color,
        fontSize: isCandidate ? '10px' : '11px',
        fontWeight: 500,
        // Allow text chip to grow in height for multi-line
        height: isTextChip ? 'auto' : (isCandidate ? '22px' : '24px'),
        minHeight: isCandidate ? '22px' : '24px',
        cursor: isClickable ? 'pointer' : 'default',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        letterSpacing: '-0.01em',
        border: `1px solid ${colors.border}`,
        ...(fullWidth && {
          width: '100%',
          justifyContent: 'flex-start'
        }),
        '& .MuiChip-icon': {
          marginLeft: '6px',
          marginRight: '-2px',
          // Align icon to top for multi-line text chips
          ...(isTextChip && {
            alignSelf: 'flex-start',
            marginTop: '4px'
          })
        },
        '& .MuiChip-label': {
          paddingLeft: '6px',
          paddingRight: '10px',
          // Allow text to wrap for text chips
          ...(isTextChip ? {
            whiteSpace: 'normal',
            lineHeight: 1.4,
            paddingTop: '4px',
            paddingBottom: '4px'
          } : fullWidth ? {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          } : {})
        },
        ...(isClickable && {
          '&:hover': {
            background: colors.hoverBg,
            transform: 'translateY(-1px)',
            boxShadow: `0 2px 8px ${colors.shadow}`
          }
        }),
        transition: 'all 0.2s ease'
      }}
    />
  );
};

export default AICandidateChip;
